import React, { useState, useEffect } from 'react';
import { cronApi } from '../api';
import { useActivity } from '../App';
import toast from 'react-hot-toast';

const JOB_TYPES = [
  { value: 'NOTIFICATION', label: '🔔 Notification Broadcast', desc: 'Send push notification to all active users' },
  { value: 'BONUS_COINS',  label: '🪙 Bonus Coins',            desc: 'Credit bonus coins to all active users'   },
  { value: 'EMAIL_BLAST',  label: '📧 Email Blast',            desc: 'Send bulk email via email service'        },
  { value: 'REPORT',       label: '📊 Generate Report',        desc: 'Auto-generate and export a report'        },
  { value: 'CUSTOM',       label: '⚙️ Custom Script',          desc: 'Placeholder for custom server-side tasks' },
];

const STATUS_CFG = {
  SCHEDULED: { color: '#22c55e', bg: 'rgba(34,197,94,0.12)',   icon: '🟢' },
  PAUSED:    { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  icon: '⏸' },
  RUNNING:   { color: '#06b6d4', bg: 'rgba(6,182,212,0.12)',   icon: '⚡' },
  READY:     { color: '#6366f1', bg: 'rgba(99,102,241,0.12)',  icon: '🕐' },
  ERROR:     { color: '#ef4444', bg: 'rgba(239,68,68,0.12)',   icon: '❌' },
};

const CRON_PRESETS = [
  { label: 'Every minute',        value: '* * * * *'   },
  { label: 'Every 5 minutes',     value: '*/5 * * * *' },
  { label: 'Every hour',          value: '0 * * * *'   },
  { label: 'Daily at 8 AM',       value: '0 8 * * *'   },
  { label: 'Daily at midnight',   value: '0 0 * * *'   },
  { label: 'Every Monday 9 AM',   value: '0 9 * * 1'   },
  { label: 'First of month',      value: '0 9 1 * *'   },
];

const JobModal = ({ job, onClose, onSave }) => {
  const isEdit = !!job;
  const [form, setForm] = useState(job || {
    name: '', type: 'NOTIFICATION', cronExpression: '0 8 * * *', enabled: true,
    params: { title: '', message: '', notifType: 'SYSTEM' },
  });
  const [saving, setSaving] = useState(false);

  const updateParam = (key, val) =>
    setForm(p => ({ ...p, params: { ...p.params, [key]: val } }));

  const handleSave = async () => {
    if (!form.name.trim())           { toast.error('Job name required');      return; }
    if (!form.cronExpression.trim()) { toast.error('Cron expression required'); return; }
    setSaving(true);
    try {
      if (isEdit) { await cronApi.update(job.id, form); toast.success('Job updated!'); }
      else        { await cronApi.create(form);          toast.success('Job created!'); }
      onSave();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save job');
    } finally { setSaving(false); }
  };

  return (
    <div style={overlayS}>
      <div style={{ ...modalS, maxWidth: '560px' }}>
        <div style={modalHeaderS}>
          <h3 style={{ fontWeight: 700 }}>{isEdit ? '✏️ Edit Job' : '➕ Create Cron Job'}</h3>
          <button style={closeBtnS} onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '24px' }}>

          {/* Name + Type */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
            <div>
              <label style={labelS}>Job Name *</label>
              <input style={inputS} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Daily Bonus" />
            </div>
            <div>
              <label style={labelS}>Job Type</label>
              <select style={inputS} value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
                {JOB_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>

          {/* Cron Expression */}
          <div style={{ marginBottom: '14px' }}>
            <label style={labelS}>Cron Expression * <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(5-field: min hr dom mon dow)</span></label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input style={{ ...inputS, flex: 1, fontFamily: 'monospace' }}
                value={form.cronExpression}
                onChange={e => setForm(p => ({ ...p, cronExpression: e.target.value }))}
                placeholder="0 8 * * *" />
              <select style={{ ...inputS, width: 'auto' }}
                onChange={e => e.target.value && setForm(p => ({ ...p, cronExpression: e.target.value }))}>
                <option value="">⚡ Presets</option>
                {CRON_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label} ({p.value})</option>)}
              </select>
            </div>
            <div style={{ marginTop: '6px', padding: '8px 10px', background: 'var(--bg-tertiary)', borderRadius: '6px', fontSize: '0.76rem', color: 'var(--text-muted)' }}>
              📖 Format: <code style={{ color: 'var(--accent-primary)' }}>minute hour day-of-month month day-of-week</code>
              &nbsp; e.g. <code>0 8 * * 1-5</code> = weekdays at 8am
            </div>
          </div>

          {/* Job-specific params */}
          {form.type === 'NOTIFICATION' && (
            <div style={{ padding: '14px', background: 'var(--bg-tertiary)', borderRadius: '10px', marginBottom: '14px' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '10px' }}>🔔 Notification Settings</div>
              <div style={{ marginBottom: '10px' }}>
                <label style={labelS}>Title</label>
                <input style={inputS} value={form.params.title || ''} onChange={e => updateParam('title', e.target.value)} placeholder="WalkBattle Update" />
              </div>
              <div>
                <label style={labelS}>Message</label>
                <textarea style={{ ...inputS, minHeight: '70px', resize: 'vertical' }} value={form.params.message || ''} onChange={e => updateParam('message', e.target.value)} placeholder="Check out what's new!" />
              </div>
            </div>
          )}

          {form.type === 'BONUS_COINS' && (
            <div style={{ padding: '14px', background: 'var(--bg-tertiary)', borderRadius: '10px', marginBottom: '14px' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '10px' }}>🪙 Bonus Coins Settings</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={labelS}>Amount (coins per user)</label>
                  <input style={inputS} type="number" value={form.params.amount || 10} onChange={e => updateParam('amount', parseInt(e.target.value))} min="1" />
                </div>
                <div>
                  <label style={labelS}>Source Label</label>
                  <input style={inputS} value={form.params.source || ''} onChange={e => updateParam('source', e.target.value)} placeholder="Daily Bonus" />
                </div>
              </div>
            </div>
          )}

          {/* Enabled toggle */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: 'var(--bg-tertiary)', borderRadius: '8px', marginBottom: '20px' }}>
            <div>
              <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>Enable Job</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Job will start running on schedule immediately</div>
            </div>
            <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ position: 'relative', width: '44px', height: '24px' }}>
                <input type="checkbox" checked={form.enabled} onChange={e => setForm(p => ({ ...p, enabled: e.target.checked }))} style={{ display: 'none' }} />
                <div style={{ width: '44px', height: '24px', borderRadius: '12px', background: form.enabled ? '#6366f1' : 'var(--border-color)', transition: 'background 0.2s', cursor: 'pointer' }}
                  onClick={() => setForm(p => ({ ...p, enabled: !p.enabled }))}>
                  <div style={{ position: 'absolute', top: '3px', left: form.enabled ? '23px' : '3px', width: '18px', height: '18px', borderRadius: '50%', background: 'white', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
                </div>
              </div>
            </label>
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button style={cancelBtnS} onClick={onClose}>Cancel</button>
            <button style={primaryBtnS} onClick={handleSave} disabled={saving}>
              {saving ? '⏳ Saving…' : (isEdit ? '✅ Save Changes' : '🚀 Create Job')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CronJobs = () => {
  const { addLog } = useActivity();
  const [jobs, setJobs]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]   = useState(null); // null | 'new' | job object

  useEffect(() => { loadJobs(); }, []);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const res = await cronApi.list();
      setJobs(res.data?.data || []);
    } catch { toast.error('Failed to load cron jobs'); }
    finally { setLoading(false); }
  };

  const handleToggle = async (job) => {
    try {
      const res = await cronApi.toggle(job.id);
      setJobs(p => p.map(j => j.id === job.id ? { ...j, ...res.data?.data } : j));
      addLog('Cron Job Toggled', job.name, 'update');
      toast.success(job.enabled ? 'Job paused' : 'Job enabled');
    } catch { toast.error('Failed to toggle'); }
  };

  const handleRunNow = async (job) => {
    try {
      toast.loading(`Running "${job.name}"…`, { id: 'run' });
      await cronApi.runNow(job.id);
      await loadJobs();
      addLog('Cron Job Run Manually', job.name, 'publish');
      toast.success(`"${job.name}" executed!`, { id: 'run' });
    } catch { toast.error('Failed to run job', { id: 'run' }); }
  };

  const handleDelete = async (job) => {
    if (!window.confirm(`Delete job "${job.name}"?`)) return;
    try {
      await cronApi.delete(job.id);
      setJobs(p => p.filter(j => j.id !== job.id));
      addLog('Cron Job Deleted', job.name, 'delete');
      toast.success('Job deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const active   = jobs.filter(j => j.enabled).length;
  const paused   = jobs.filter(j => !j.enabled).length;

  return (
    <div className="animate-in">
      <div className="page-header">
        <div className="page-header-left">
          <div className="breadcrumb"><span>System</span> / <span className="current">Cron Jobs</span></div>
          <h1>Cron Job Scheduler</h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            Schedule automated tasks — notifications, bonus coins, email blasts & reports
          </p>
        </div>
        <button style={primaryBtnS} onClick={() => setModal('new')}>➕ Create Job</button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '14px', marginBottom: '24px' }}>
        {[
          { label: 'Total Jobs',    value: jobs.length, color: 'var(--accent-primary)', icon: '📋' },
          { label: 'Active',        value: active,       color: '#22c55e',               icon: '🟢' },
          { label: 'Paused',        value: paused,       color: '#f59e0b',               icon: '⏸' },
        ].map((s, i) => (
          <div key={i} style={{ ...cardS, display: 'flex', alignItems: 'center', gap: '16px', textAlign: 'left' }}>
            <span style={{ fontSize: '2.2rem' }}>{s.icon}</span>
            <div>
              <div style={{ fontSize: '1.6rem', fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Job type info */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px', marginBottom: '28px' }}>
        {JOB_TYPES.map(t => (
          <div key={t.value} style={{ padding: '12px 14px', background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '10px' }}>
            <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{t.label}</div>
            <div style={{ fontSize: '0.73rem', color: 'var(--text-muted)', marginTop: '3px' }}>{t.desc}</div>
          </div>
        ))}
      </div>

      {/* Jobs list */}
      {loading ? (
        <div style={{ ...cardS, textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>Loading…</div>
      ) : jobs.length === 0 ? (
        <div style={{ ...cardS, textAlign: 'center', padding: '60px' }}>
          <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🕐</div>
          <div style={{ fontWeight: 700, marginBottom: '8px' }}>No scheduled jobs yet</div>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '20px' }}>
            Create your first cron job to automate tasks like daily notifications or bonus coins
          </div>
          <button style={primaryBtnS} onClick={() => setModal('new')}>➕ Create First Job</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {jobs.map(job => {
            const statusCfg = STATUS_CFG[job.status] || STATUS_CFG.READY;
            const typeMeta  = JOB_TYPES.find(t => t.value === job.type);
            return (
              <div key={job.id} style={{ ...cardS, display: 'flex', alignItems: 'center', gap: '16px', padding: '18px 20px',
                textAlign: 'left', borderLeft: `4px solid ${job.enabled ? '#22c55e' : 'var(--border-color)'}` }}>

                {/* Status dot */}
                <div style={{ flexShrink: 0, fontSize: '1.4rem' }}>{statusCfg.icon}</div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{job.name}</span>
                    <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: '20px',
                      background: statusCfg.bg, color: statusCfg.color, fontWeight: 600 }}>
                      {job.status}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '16px', fontSize: '0.78rem', color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                    <span>{typeMeta?.label || job.type}</span>
                    <span style={{ fontFamily: 'monospace', background: 'var(--bg-tertiary)', padding: '1px 6px', borderRadius: '4px', color: 'var(--accent-primary)' }}>
                      ⏱ {job.cronExpression}
                    </span>
                    {job.lastRun && (
                      <span>Last: {new Date(job.lastRun).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                    )}
                    <span>Created: {new Date(job.createdAt).toLocaleDateString('en-IN')}</span>
                  </div>
                  {/* Params preview */}
                  {job.params && Object.keys(job.params).length > 0 && (
                    <div style={{ marginTop: '6px', fontSize: '0.73rem', color: 'var(--text-muted)' }}>
                      {Object.entries(job.params).filter(([,v]) => v !== '' && v !== null && v !== undefined).map(([k,v]) => (
                        <span key={k} style={{ marginRight: '10px' }}><strong>{k}:</strong> {String(v)}</span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <button style={{ ...actionBtnS, color: job.enabled ? '#f59e0b' : '#22c55e' }}
                    onClick={() => handleToggle(job)} title={job.enabled ? 'Pause' : 'Enable'}>
                    {job.enabled ? '⏸ Pause' : '▶ Enable'}
                  </button>
                  <button style={{ ...actionBtnS, color: '#06b6d4' }}
                    onClick={() => handleRunNow(job)} title="Run immediately">
                    ⚡ Run Now
                  </button>
                  <button style={actionBtnS} onClick={() => setModal(job)} title="Edit">✏️</button>
                  <button style={{ ...actionBtnS, color: '#ef4444' }}
                    onClick={() => handleDelete(job)} title="Delete">🗑️</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal !== null && (
        <JobModal
          job={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSave={loadJobs}
        />
      )}
    </div>
  );
};

const cardS       = { background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '18px' };
const overlayS    = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' };
const modalS      = { background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '16px', width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg)' };
const modalHeaderS= { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid var(--border-color)' };
const closeBtnS   = { background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1.2rem' };
const inputS      = { padding: '9px 14px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '0.88rem', outline: 'none', width: '100%', boxSizing: 'border-box' };
const labelS      = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' };
const primaryBtnS = { padding: '9px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white', display: 'inline-flex', alignItems: 'center', gap: '6px' };
const cancelBtnS  = { padding: '9px 18px', borderRadius: '8px', border: '1px solid var(--border-color)', cursor: 'pointer', fontSize: '0.88rem', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' };
const actionBtnS  = { padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', cursor: 'pointer', fontSize: '0.8rem', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontWeight: 500 };

export default CronJobs;
